import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  ArrowLeft,
  ArrowRight,
  User,
  CheckCircle2,
  Loader2,
  Send,
  Sparkles,
  AlertCircle,
  Clock,
  Monitor,
  MapPin,
} from 'lucide-react';
import { toast } from 'sonner';
import { isWorkplaceMode } from '@/lib/experienceStatus';
import ConsentPrompt from '@/components/ConsentPrompt';

const STEPS = ['Select Child', 'Choose Mode', 'Review & Submit'];

function StepIndicator({ current, steps }) {
  return (
    <div className="flex items-center gap-1 mb-8" data-testid="step-indicator">
      {steps.map((step, i) => (
        <React.Fragment key={i}>
          <div className="flex items-center gap-1.5">
            <div
              className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${
                i < current
                  ? 'bg-[#F97D6A] text-white'
                  : i === current
                  ? 'bg-[#F97D6A] text-white ring-4 ring-[#F97D6A]/20'
                  : 'bg-gray-100 text-gray-400'
              }`}
            >
              {i < current ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
            </div>
            <span
              className={`text-xs font-medium hidden sm:block ${
                i <= current ? 'text-[#1F2937]' : 'text-gray-400'
              }`}
            >
              {step}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div className={`flex-1 h-0.5 mx-1 rounded-full ${i < current ? 'bg-[#F97D6A]' : 'bg-gray-200'}`} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

export default function BookingFlowPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [career, setCareer] = useState(null);
  const [children, setChildren] = useState([]);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [consentAsk, setConsentAsk] = useState(null);   // { doc, reason }
  const [submitted, setSubmitted] = useState(false);

  const [selectedChildId, setSelectedChildId] = useState('');
  const [selectedMode, setSelectedMode] = useState('');
  const [availableModes, setAvailableModes] = useState([]);
  const [modesLoading, setModesLoading] = useState(false);
  const [offlineAgreed, setOfflineAgreed] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [careerRes, childrenRes] = await Promise.all([
        api.get(`/careers/${slug}`),
        api.get('/children'),
      ]);
      setCareer(careerRes.data);
      const approved = childrenRes.data.filter((c) => c.status === 'approved');
      setChildren(approved);
      if (approved.length === 1) setSelectedChildId(approved[0].id);
    } catch {
      toast.error('Failed to load data');
      navigate(`/careers/${slug}`);
    } finally {
      setLoading(false);
    }
  }, [slug, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleNext = () => {
    if (step === 0 && !modesLoading && availableModes.length === 0) {
      // Fetch available modes when moving from child selection
      fetchModes();
    }
    if (step < STEPS.length - 1) setStep(step + 1);
  };
  const handleBack = () => { if (step > 0) setStep(step - 1); else navigate(`/careers/${slug}`); };

  const fetchModes = async () => {
    setModesLoading(true);
    try {
      const res = await api.get(`/careers/${slug}/available-modes`);
      const modes = res.data.modes || [];
      // Online is always offered; finding someone is the school's job, after the request arrives
      setAvailableModes(['online', ...modes.filter(m => m !== 'online')]);
    } catch {
      setAvailableModes(['online']);
      setSelectedMode('online');
    }
    setModesLoading(false);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await api.post('/bookings', {
        child_id: selectedChildId,
        career_slug: slug,
        requested_mode: selectedMode || 'online',
      });
      setSubmitted(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 409 && d?.consent_required) {
        // First time for this child: ask for the permission here rather than in a separate step
        const doc = (await api.get(`/consent/documents/${d.consent_required}`)).data;
        setConsentAsk({ doc, reason: d.reason });
      } else {
        toast.error(typeof d === 'string' ? d : 'Failed to submit request');
      }
    }
    setSubmitting(false);
  };

  const acceptConsent = async () => {
    setSubmitting(true);
    try {
      await api.post('/parent/permissions/accept', {
        document_id: consentAsk.doc.id, child_id: selectedChildId,
      });
      setConsentAsk(null);
      setSubmitting(false);
      await handleSubmit();
      return;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not record the permission');
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="py-6 sm:py-8">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <div className="animate-pulse space-y-6">
            <div className="h-8 w-40 bg-gray-200 rounded" />
            <div className="h-32 bg-gray-100 rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  if (!career) return null;

  const consentDialog = (
    <ConsentPrompt doc={consentAsk?.doc} open={!!consentAsk} reason={consentAsk?.reason}
                   onOpenChange={() => setConsentAsk(null)} onAccept={acceptConsent} busy={submitting} />
  );

  const selectedChild = children.find((c) => c.id === selectedChildId);
  const pendingChildren = children.length === 0;

  // One confirmation, whatever happened behind it
  if (submitted) {
    return (
      <div className="py-6 sm:py-8 min-h-[calc(100vh-4rem)]" data-testid="booking-flow-page">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center" data-testid="request-confirmation">
            <div className="w-16 h-16 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-8 w-8 text-emerald-500" />
            </div>
            <h2 className="text-xl font-bold text-[#1F2937] font-['Outfit'] mb-2">
              Request Received
            </h2>
            <p className="text-sm text-[#6B7280] max-w-sm mx-auto mb-6">
              Your school has your request for {career?.name}. They will come back to you with a time to confirm.
            </p>
            <div className="flex gap-3 justify-center">
              <Button onClick={() => navigate('/careers')} variant="outline" className="rounded-xl" data-testid="confirmation-explore-btn">
                Explore Other Careers
              </Button>
              <Button onClick={() => navigate('/parent')} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl" data-testid="confirmation-dashboard-btn">
                Go to Dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }


  return (
    <>
    <div className="py-6 sm:py-8 min-h-[calc(100vh-4rem)]" data-testid="booking-flow-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        <Button
          variant="ghost"
          onClick={handleBack}
          className="text-sm text-[#6B7280] hover:text-[#1F2937] mb-4 -ml-2 rounded-lg"
          data-testid="booking-back-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-1.5" />
          {step === 0 ? 'Back to Career' : 'Back'}
        </Button>

        {/* Career context bar */}
        <div className="flex items-center gap-3 mb-6 p-3 bg-white rounded-xl border border-gray-200">
          <img src={career.image_card_url || career.image_hero_url || career.image_url} alt={career.name} className="w-12 h-12 rounded-lg object-cover" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-[#1F2937] font-['Outfit'] truncate" data-testid="booking-career-name">{career.name}</p>
            <p className="text-xs text-[#9CA3AF]">{career.category}</p>
          </div>
          <Badge className="bg-[#FFF0ED] text-[#F97D6A] border-0 text-[10px] font-bold shrink-0">Request Experience</Badge>
        </div>

        <StepIndicator current={step} steps={STEPS} />

        {/* Step 0: Select Child */}
        {step === 0 && (
          <div data-testid="step-select-child">
            <h2 className="text-xl font-bold text-[#1F2937] font-['Outfit'] mb-2">Who is this experience for?</h2>
            <p className="text-sm text-[#6B7280] mb-6">Select the child who will attend this career experience session.</p>
            {pendingChildren ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
                <div className="w-14 h-14 rounded-2xl bg-amber-50 flex items-center justify-center mx-auto mb-3">
                  <AlertCircle className="h-7 w-7 text-amber-500" />
                </div>
                <p className="text-sm font-semibold text-[#1F2937] mb-1">No approved children</p>
                <p className="text-xs text-[#9CA3AF] mb-4">Your children need to be approved by the Career Coordinator before requesting experiences.</p>
                <Button onClick={() => navigate('/parent')} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl" data-testid="go-dashboard-btn">
                  Go to Dashboard
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {children.map((child) => (
                  <div
                    key={child.id}
                    onClick={() => setSelectedChildId(child.id)}
                    className={`bg-white rounded-2xl border-2 p-5 cursor-pointer transition-all ${
                      selectedChildId === child.id
                        ? 'border-[#F97D6A] shadow-md shadow-[#F97D6A]/10'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid={`child-option-${child.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 transition-colors ${selectedChildId === child.id ? 'bg-[#F97D6A]' : 'bg-[#FFF0ED]'}`}>
                        <User className={`h-5 w-5 ${selectedChildId === child.id ? 'text-white' : 'text-[#F97D6A]'}`} />
                      </div>
                      <div className="flex-1">
                        <p className="text-base font-semibold text-[#1F2937] font-['Outfit']">{child.full_name}</p>
                        <p className="text-xs text-[#9CA3AF]">Grade {child.grade}</p>
                      </div>
                      {selectedChildId === child.id && <CheckCircle2 className="h-5 w-5 text-[#F97D6A] shrink-0" />}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 1: Choose Mode */}
        {step === 1 && (
          <div data-testid="step-choose-mode">
            <h2 className="text-xl font-bold text-[#1F2937] font-['Outfit'] mb-2">How would you like to experience this?</h2>
            <p className="text-sm text-[#6B7280] mb-6">Select your preferred session type.</p>
            {modesLoading ? (
              <div className="py-8 text-center">
                <Loader2 className="h-6 w-6 animate-spin text-[#F97D6A] mx-auto mb-2" />
                <p className="text-xs text-[#9CA3AF]">Checking available options...</p>
              </div>
            ) : (
              <div className="space-y-3">
                <div
                  onClick={() => availableModes.includes('online') && setSelectedMode('online')}
                  className={`bg-white rounded-2xl border-2 p-5 transition-all ${
                    !availableModes.includes('online')
                      ? 'opacity-50 cursor-not-allowed border-gray-200'
                      : selectedMode === 'online'
                      ? 'border-[#F97D6A] shadow-md shadow-[#F97D6A]/10 cursor-pointer'
                      : 'border-gray-200 hover:border-gray-300 cursor-pointer'
                  }`}
                  data-testid="mode-option-online"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 transition-colors ${selectedMode === 'online' ? 'bg-[#F97D6A]' : 'bg-blue-50'}`}>
                      <Monitor className={`h-5 w-5 ${selectedMode === 'online' ? 'text-white' : 'text-blue-500'}`} />
                    </div>
                    <div className="flex-1">
                      <p className="text-base font-semibold text-[#1F2937] font-['Outfit']">Online Session</p>
                      <p className="text-xs text-[#9CA3AF]">Video call session with a career mentor</p>
                    </div>
                    {selectedMode === 'online' && <CheckCircle2 className="h-5 w-5 text-[#F97D6A] shrink-0" />}
                    {!availableModes.includes('online') && <Badge className="text-[9px] bg-gray-100 text-gray-500 border-0 shrink-0">Unavailable</Badge>}
                  </div>
                </div>

                <div
                  onClick={() => availableModes.some(isWorkplaceMode) && setSelectedMode('workplace_visit')}
                  className={`bg-white rounded-2xl border-2 p-5 transition-all ${
                    !availableModes.some(isWorkplaceMode)
                      ? 'opacity-50 cursor-not-allowed border-gray-200'
                      : isWorkplaceMode(selectedMode)
                      ? 'border-[#F97D6A] shadow-md shadow-[#F97D6A]/10 cursor-pointer'
                      : 'border-gray-200 hover:border-gray-300 cursor-pointer'
                  }`}
                  data-testid="mode-option-offline"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 transition-colors ${isWorkplaceMode(selectedMode) ? 'bg-[#F97D6A]' : 'bg-emerald-50'}`}>
                      <MapPin className={`h-5 w-5 ${isWorkplaceMode(selectedMode) ? 'text-white' : 'text-emerald-500'}`} />
                    </div>
                    <div className="flex-1">
                      <p className="text-base font-semibold text-[#1F2937] font-['Outfit']">Workplace Visit</p>
                      <p className="text-xs text-[#9CA3AF]">Visit a mentor at their workplace</p>
                    </div>
                    {isWorkplaceMode(selectedMode) && <CheckCircle2 className="h-5 w-5 text-[#F97D6A] shrink-0" />}
                    {!availableModes.some(isWorkplaceMode) && <Badge className="text-[9px] bg-gray-100 text-gray-500 border-0 shrink-0">Unavailable</Badge>}
                  </div>
                  {availableModes.some(isWorkplaceMode) && (
                    <div className="mt-3 ml-16">
                      <div className="bg-amber-50 rounded-lg p-3" data-testid="offline-safety-notice">
                        <p className="text-[11px] text-amber-700 font-medium mb-2">
                          An approved mentor or workplace host supervises students throughout the visit. Your parental
                          consent is required, and you handle drop-off and pick-up unless the school arranges transport.
                        </p>
                        <div className="flex items-start gap-2">
                          <Checkbox
                            id="offline-agree"
                            checked={offlineAgreed}
                            onCheckedChange={(checked) => setOfflineAgreed(!!checked)}
                            className="mt-0.5"
                            data-testid="offline-agree-checkbox"
                          />
                          <label htmlFor="offline-agree" className="text-[11px] text-amber-800 cursor-pointer font-semibold">
                            I understand and agree
                          </label>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

              </div>
            )}
          </div>
        )}

        {/* Step 2: Review & Submit */}
        {step === 2 && (
          <div data-testid="step-review">
            <h2 className="text-xl font-bold text-[#1F2937] font-['Outfit'] mb-2">Review your request</h2>
            <p className="text-sm text-[#6B7280] mb-6">Confirm the details below and submit your experience request.</p>
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <div className="p-5 space-y-4">
                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                  <span className="text-sm text-[#9CA3AF]">Career</span>
                  <span className="text-sm font-semibold text-[#1F2937]" data-testid="review-career">{career.name}</span>
                </div>
                <div className="flex items-center justify-between py-3 border-b border-gray-100">
                  <span className="text-sm text-[#9CA3AF]">Child</span>
                  <span className="text-sm font-semibold text-[#1F2937]" data-testid="review-child">
                    {selectedChild?.full_name} (Grade {selectedChild?.grade})
                  </span>
                </div>
                <div className="flex items-center justify-between py-3">
                  <span className="text-sm text-[#9CA3AF]">Mode</span>
                  <Badge className={`text-xs font-semibold border-0 rounded-full px-3 py-1 ${
                    selectedMode === 'online' ? 'bg-blue-50 text-blue-700' : 'bg-emerald-50 text-emerald-700'
                  }`} data-testid="review-mode">
                    {selectedMode === 'online' ? 'Online' : 'Workplace Visit'}
                  </Badge>
                </div>
              </div>
              <div className="bg-[#F5F6F8] px-5 py-3 border-t border-gray-100">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[#F97D6A]" />
                  <span className="text-sm text-[#6B7280]">
                    Your coordinator will match a mentor and schedule the {selectedMode === 'online' ? 'video call' : 'workplace visit'}.
                  </span>
                </div>
              </div>
            </div>

            {/* Offline confirmation */}
            {isWorkplaceMode(selectedMode) && (
              <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3" data-testid="offline-confirm-notice">
                <AlertCircle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800 font-medium">
                  By confirming, you give consent for this workplace visit, where an approved mentor or workplace host
                  supervises the students. You arrange drop-off and pick-up, unless the school tells you it is
                  arranging transport.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Bottom Actions */}
        <div className="mt-8 flex items-center gap-3" data-testid="booking-actions">
          {step > 0 && (
            <Button variant="outline" onClick={handleBack} className="rounded-xl h-12 px-6 border-gray-200" data-testid="booking-prev-btn">
              <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
            </Button>
          )}
          <div className="flex-1" />
          {step < STEPS.length - 1 ? (
            <Button
              onClick={handleNext}
              disabled={(step === 0 && !selectedChildId) || (step === 1 && !selectedMode) || (step === 1 && isWorkplaceMode(selectedMode) && !offlineAgreed)}
              className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl h-12 px-8 text-sm font-semibold disabled:opacity-40"
              data-testid="booking-next-btn"
            >
              Continue <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl h-12 px-8 text-sm font-semibold disabled:opacity-60"
              data-testid="booking-submit-btn"
            >
              {submitting ? (
                <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Submitting...</>
              ) : (
                <><Send className="h-4 w-4 mr-1.5" /> Submit Request</>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
      {consentDialog}
    </>
  );
}
